import { useEffect, useRef, useState } from 'react'
import { api, type LayoutAsset } from '../lib/api'
import { slipPicture } from '../lib/pictures'
import { uploadRefusal } from '../lib/signageRefusals'
import { explain } from '../lib/staffErrors'
import { Button } from '@heroui/react'
import { Icon } from '../ui/icons'
import { IconButton, Notice, StudioFilePicker, StudioModal } from './heroParts'
import { pictureDeleteNext } from './names'
import type { LayoutKind } from './types'
import { useStaffText } from '../ui/StaffText'

/**
 * The file for a new picture, chosen before the picture exists: a picture with no
 * file is a draft the server refuses, and every edit after it would be lost.
 * Cancel adds nothing.
 */
export function PicturePicker({
  kind,
  title,
  onPick,
  onClose,
}: {
  kind: LayoutKind
  /** The dialog's heading; a picture's by default. */
  title?: string
  onPick: (asset: LayoutAsset) => void
  onClose: () => void
}) {
  const { say, lang } = useStaffText()
  const [assets, setAssets] = useState<LayoutAsset[] | null>(null)
  const [uploading, setUploading] = useState(false)
  const [refused, setRefused] = useState('')
  // The picture asked about, and the old versions the server said go with it.
  const [asking, setAsking] = useState<{ asset: LayoutAsset; versions: number } | null>(null)
  const [deleting, setDeleting] = useState(false)
  // An upload finishes renders later; the latest onPick holds the document as it is then.
  const pick = useRef(onPick)
  pick.current = onPick
  // Cancelled while a file was on its way: the finished upload adds nothing.
  const live = useRef(true)

  useEffect(() => {
    live.current = true
    api
      .assets()
      .then((r) => live.current && setAssets(r.assets.filter((a) => a.contentType.startsWith('image/'))))
      .catch(() => live.current && setAssets([]))
    return () => {
      live.current = false
    }
  }, [])

  async function upload(file: File) {
    setUploading(true)
    setRefused('')
    try {
      // As the inspector does: a slip's picture is shrunk to the paper first.
      const asset = kind === 'ticket' ? await api.uploadAsset(await slipPicture(file), 'slip') : await api.uploadAsset(file, 'picture')
      if (!live.current) return
      if (!asset.contentType.startsWith('image/')) {
        setRefused(say('designs.insp.isFilm'))
        return
      }
      pick.current(asset)
    } catch (err) {
      if (live.current) setRefused(uploadRefusal(err, lang))
    } finally {
      if (live.current) setUploading(false)
    }
  }

  async function remove(ask: { asset: LayoutAsset; versions: number }) {
    setDeleting(true)
    setRefused('')
    let failed: unknown = null
    try {
      await api.deletePicture(ask.asset.id, ask.versions)
    } catch (err) {
      failed = err
    }
    if (!live.current) return
    setDeleting(false)
    const next = pictureDeleteNext(failed)
    if (next.step === 'done') {
      setAssets((list) => list?.filter((a) => a.id !== ask.asset.id) ?? null)
      setAsking(null)
    } else if (next.step === 'versions') {
      setAsking({ asset: ask.asset, versions: next.n })
    } else {
      setAsking(null)
      setRefused(explain(failed, lang, 'common.unreachable'))
    }
  }

  return (
    <StudioModal
      size="lg"
      title={title ?? say('designs.el.image')}
      onClose={onClose}
      footer={
        <>
          <StudioFilePicker
            accept="image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif"
            label={say('designs.media.upload')}
            disabled={uploading}
            onFile={(file) => void upload(file)}
          />
          <span className="studio-spacer" />
          <Button variant="tertiary" onPress={onClose}>
            {say('common.cancel')}
          </Button>
        </>
      }
    >
          {refused && <Notice>{refused}</Notice>}
          {assets === null ? (
            <small>{say('common.loading')}</small>
          ) : (
            assets.length > 0 && (
              <ul className="studio-pics">
                {assets.map((a) => (
                  <li key={a.id} className="studio-pics__item">
                    <button type="button" className="studio-pic" disabled={uploading} onClick={() => onPick(a)} title={a.filename}>
                      <img src={api.assetURL(a.id)} alt="" loading="lazy" />
                      <span>{a.filename}</span>
                    </button>
                    {!a.library && (
                      <IconButton
                        tip={say('common.delete')}
                        variant="secondary"
                        className="studio-pic__delete"
                        isDisabled={uploading || deleting}
                        aria-label={say('designs.page.deleteName', { name: a.filename })}
                        onPress={() => {
                          setRefused('')
                          setAsking({ asset: a, versions: 0 })
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </IconButton>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
          {asking && (
            <div className="studio-pics__ask" role="group" aria-label={say('common.delete')}>
              <span>{say('designs.page.deleteName', { name: asking.asset.filename })}</span>
              <span className="studio-spacer" />
              <Button variant="tertiary" size="sm" onPress={() => setAsking(null)}>
                {say('common.cancel')}
              </Button>
              <Button variant="danger" size="sm" isDisabled={deleting} onPress={() => void remove(asking)}>
                {asking.versions > 0 ? say('designs.media.alsoVersions', { n: asking.versions }) : say('common.delete')}
              </Button>
            </div>
          )}
    </StudioModal>
  )
}
